import { MouseEvent, useEffect, useState } from 'react';
import * as Menubar from '@radix-ui/react-menubar';
import classNames from 'classnames';

import { initMavenoidApiClient } from '@gn-audio/core/auth/mavenoid';
import { getWebStoreApiClient } from '@gn-audio/core/auth/web-store-api';
import { icon_basket, icon_device_menu, icon_log_out, icon_person } from '@gn-audio/icons';

import { GNElliotWeb } from '../../public/fonts/fonts';
import useFallbackTranslation from '../../utils/hooks/useFallbackTranslation';
import { useBasket } from '../Basket/BasketContext/useBasket';
import { FeatureToggle } from '../FeatureToggle';
import { JabraIcon } from '../JabraIcon/JabraIcon';
import { useHeaderNavigation } from '../MegaMenu';
import { getAccountDetails } from './utilities/get-webstore-account-details';
import { logIn, PostLoginAction } from './utilities/logIn';
import { UipMenuItem } from './UipMenuItem';

type accountData = {
  initials: string;
  title: string;
};

export const UipMenu = () => {
  const { t } = useFallbackTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [accountData, setAccountData] = useState<accountData>();
  const { basket } = useBasket();

  useEffect(() => {
    getAccountDetails().then((data) => {
      if (data) {
        setAccountData(data);
        setIsLoggedIn(true);
      }
    });
    initMavenoidApiClient();
  }, []);

  const logInHandler = async () => {
    if (!isLoggedIn) {
      let postLoginActions: PostLoginAction[] = [];
      if (basket) {
        postLoginActions = [
          {
            type: 'transferBasket',
            params: { basketId: basket?.basket_id, customerId: basket?.customer_info?.customer_id },
          },
        ];
      }
      await logIn(postLoginActions);
    }
  };

  const logOutHandler = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    getWebStoreApiClient().signOut();
  };

  const { myJabraUrl } = useHeaderNavigation();

  return isLoggedIn ? (
    <FeatureToggle featureName="TopMenuUipEnabled">
      <Menubar.Root>
        <Menubar.Menu>
          <Menubar.Trigger>
            <button
              className="flex h-12 items-center justify-center rounded-full px-3 hover:bg-gray-100"
              aria-label={t('uip.YourAccount')}
              title={accountData?.title}
            >
              <div
                className={classNames(
                  'btn-top-menu size-6 bg-black p-2 text-sm text-white hover:bg-black'
                )}
              >
                {accountData?.initials}
              </div>
            </button>
          </Menubar.Trigger>
          <Menubar.Portal>
            <Menubar.Content
              sideOffset={5}
              alignOffset={-50}
              align="end"
              className={classNames(
                'z-50 w-screen cursor-pointer rounded-md border border-t-0 border-gray-50 bg-white pb-1 shadow-2xl lg:w-56',
                GNElliotWeb.className
              )}
            >
              <Menubar.Label className="pb-6 pl-5 pr-5 pt-6 text-[0.9rem] font-semibold uppercase">
                {t('uip.YourAccount')}
              </Menubar.Label>
              <a className="hover:no-underline" href={myJabraUrl || '/'}>
                <UipMenuItem icon={icon_device_menu} label={t('uip.MyJabraAccount')} />
              </a>
              <a className="hover:no-underline" href={myJabraUrl ? `${myJabraUrl}/profile` : '/'}>
                <UipMenuItem icon={icon_person} label={t('uip.EditProfile')} />
              </a>
              <a className="hover:no-underline" href={myJabraUrl ? `${myJabraUrl}/orders` : '/'}>
                <UipMenuItem icon={icon_basket} label={t('uip.OrderHistory')} />
              </a>
              <UipMenuItem
                icon={icon_log_out}
                label={t('uip.Logout')}
                onClick={(event) => logOutHandler(event)}
              />
              <Menubar.Arrow width={20} height={10} className="fill-white drop-shadow-sm" />
            </Menubar.Content>
          </Menubar.Portal>
        </Menubar.Menu>
      </Menubar.Root>
    </FeatureToggle>
  ) : (
    <>
      <button
        title={t('uip.SignIn')}
        aria-label={t('uip.SignIn')}
        onClick={() => logInHandler()}
        className="btn-top-menu"
      >
        <JabraIcon className="size-6" Icon={icon_person} />
      </button>
    </>
  );
};

export default UipMenu;
